import { Directive, inject } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { BookingService } from '@services/feature/booking.service';
import { LoggerService } from '@services/utils/logger.service';

/**
 * Directive to protect input and textarea fields from phishing links.
 * It reads dynamic settings from widgetUtilService:
 *  - enable_phishing_protection (boolean): to enable/disable validation
 *  - phishing_protection_regex (string): regex pattern to validate against
 */
@Directive({
  selector: '[phishingProtection]',
  standalone: true,
  providers: [{ provide: NG_VALIDATORS, useExisting: PhishingProtectionDirective, multi: true }]
})
export class PhishingProtectionDirective implements Validator {

  private bookingService = inject(BookingService);

  validate(control: AbstractControl): ValidationErrors | null {

    const isProtectionEnabled = this.bookingService.partnerData?.enable_phishing_protection;
    const phishingRegexPattern = this.bookingService.partnerData?.phishing_protection_regex;

    // If protection feature is disabled, skip validation completely
    if (!isProtectionEnabled || !phishingRegexPattern) {
      return null;
    }
    let safeTextRegex: RegExp;

    try {
      // Remove starting and ending slashes, and extract pattern and flags
      const match = phishingRegexPattern.match(/^\/(.*)\/([a-z]*)$/i);

      if (match) {
        const pattern = match[1]; // Everything inside /.../
        const flags = match[2];   // Flags like i, g, m etc.

        safeTextRegex = new RegExp(pattern, flags);
      } else {
        // fallback: if no slashes found, use as simple pattern
        safeTextRegex = new RegExp(phishingRegexPattern, 'i');
      }
    } catch (error) {
      LoggerService.warn('Invalid phishing protection regex configured:', phishingRegexPattern, error);
      return null;
    }

    // If input value exists and does NOT match safe text regex ➔ Mark as invalid
    if (control.value && !safeTextRegex.test(control.value)) {
      return { phishingLinkDetected: true }; // Custom validation error
    }
    // Otherwise, input is valid
    return null;
  }
}
